import { inject, Injectable } from "@angular/core";
import { NGXLogger } from "ngx-logger";
import { HttpClient } from "@angular/common/http";
import { lastValueFrom } from "rxjs";
import {
  ENVIRONMENT,
  Box,
  BoxType,
  Category,
  Material,
  Preset,
  Subcategory,
  PaperType,
  PrintType,
  SecondaryPaperType,
  Variant,
  PricingResponse,
} from "@company-box/models";
import { formatCurrency, formatNumber } from "@angular/common";

@Injectable({
  providedIn: "root",
})
export class BoxService {
  protected httpClient: HttpClient = inject(HttpClient);
  protected environment: any = inject(ENVIRONMENT);
  protected logger: NGXLogger = inject(NGXLogger);

  constructor() {}

  getPrice(
    boxCode: string,
    boardStrength: string,
    paperType: string,
    length: number,
    width: number,
    depth: number,
    quantity: number,
    twoSided: boolean,
    luxePrint: boolean,
    gloss: boolean,
    noPrint: boolean,
    options: string,
    expedited: boolean
  ): Promise<Box> {
    return new Promise<Box>((resolve, reject) => {
      let url = `${this.environment.apiHost}/box/price/${boxCode}/${boardStrength}/${paperType}?length=${length}&width=${width}&depth=${depth}&quantity=${quantity}&rush=${expedited}`;

   if (noPrint) {
     url += `&noPrint=${noPrint}`;
   } else {
     url += `&twoSided=${twoSided}&luxe=${luxePrint}&gloss=${gloss}&options=${options}`;
   }

      url += `&debug=true`;

      this.httpClient.get(url).subscribe(
        (res) => {
          const price = new Box().deserialize(res);
          this.logger.debug("price", price);
          resolve(price);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listPrices(
    boxCode: string,
    boardStrength: string,
    paperType: string,
    length: number,
    width: number,
    depth: number,
    quantities: number[],
    twoSided: boolean,
    luxePrint: boolean,
    noPrint: boolean,
    options: string,
    expedited: boolean
  ): Promise<PricingResponse> {
    return new Promise<PricingResponse>((resolve, reject) => {
      let url = `${
        this.environment.apiHost
      }/box/price/${boxCode}/${boardStrength}/${paperType}/list?length=${length}&width=${width}&depth=${depth}&quantities=${quantities.join(
        ","
      )}&rush=${expedited}`;

      if (noPrint) {
        url += `&noPrint=${noPrint}`;
      } else {
        url += `&twoSided=${twoSided}&luxe=${luxePrint}`;
      }
      url += `&includeSubcategories=true&options=${options}&debug=true`;

      this.httpClient.get(url).subscribe(
        (res) => {
          const response = new PricingResponse().deserialize(res);
          resolve(response);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  getType(code: string): Promise<BoxType> {
    return new Promise<BoxType>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/type/${code}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const box = new BoxType().deserialize(res);
          this.logger.debug("box", box);
          resolve(box);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listTypes(): Promise<BoxType[]> {
    return new Promise<BoxType[]>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/type/all`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const boxes: any[] = [];
          (res as any[]).forEach((value) => {
            const box = new BoxType().deserialize(value);
            boxes.push(box);
          });
          this.logger.debug("boxes", boxes);
          resolve(boxes);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  getCategory(categoryCode: string, includeSubcategories: boolean): Promise<Category> {
    return new Promise<Category>((resolve, reject) => {
      let url = `${this.environment.apiHost}/box/category/${categoryCode}?`;
      if (includeSubcategories) {
        url += `includeCategories=true&`;
      }
      this.httpClient.get(url).subscribe(
        (res) => {
          const category = new Category().deserialize(res);
          this.logger.debug("category", category);
          resolve(category);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }
  getSubcategory(subcategoryCode: string): Promise<Subcategory> {
    return new Promise<Subcategory>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/subcategory/${subcategoryCode}`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const subcategory = new Subcategory().deserialize(res);
          this.logger.debug("subcategory", subcategory);
          resolve(subcategory);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }
  getVariants(subcategoryId: number) {
    const url = `${this.environment.apiHost}/box/category/subcategory/${subcategoryId}/list?includeSubcategory=true`;

    return new Promise<any>((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          const variants: Variant[] = [];
          (res as any[]).forEach((value) => {
            const variant = new Variant().deserialize(value);
            variants.push(variant);
          });
          resolve(variants);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }
  listMaterials(includeStrengths?: boolean): Promise<Material[]> {
    return new Promise<Material[]>((resolve, reject) => {
      let url = `${this.environment.apiHost}/box/material/list?`;
      if (includeStrengths) {
        url += `includeStrengths=${includeStrengths}&`;
      }

      this.httpClient.get(url).subscribe(
        (res) => {
          const materials: any[] = [];
          (res as any[]).forEach((value) => {
            const material = new Material().deserialize(value);
            materials.push(material);
          });
          this.logger.debug("materials", materials);
          resolve(materials);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listCategories(includeSubcategories: boolean): Promise<Category[]> {
    return new Promise<Category[]>((resolve, reject) => {
      let url = `${this.environment.apiHost}/box/category/list?`;
      if (includeSubcategories) {
        url += `includeCategories=true&`;
      }

      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          const categories: any[] = [];
          (res as any[]).forEach((value) => {
            const category = new Category().deserialize(value);
            categories.push(category);
          });
          this.logger.debug("categories", categories);
          resolve(categories);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  listPresets(categoryCode: string, subcategoryCode: string): Promise<Preset[]> {
    return new Promise<Preset[]>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/category/${categoryCode}/${subcategoryCode}/presets/list`;
      this.httpClient.get(url).subscribe(
        (res) => {
          const presets: any[] = [];
          (res as any[]).forEach((value) => {
            const preset = new Preset().deserialize(value);
            presets.push(preset);
          });
          // this.logger.debug('presets', presets);
          resolve(presets);
        },
        (error) => {
          this.logger.debug(error.message || error);
          reject(error.message || error);
        }
      );
    });
  }

  getPaperTypes(selectedSubcategory: Subcategory): PaperType[] {
    // console.log('getPaperTypes', selectedSubcategory.code);
    const paperTypes = [];
    if (selectedSubcategory.displayPremiumWhite) {
      const paperType = new PaperType().deserialize({
        id: "pkw",
        name: "Premium White",
      });
      if (selectedSubcategory.displayStandard) {
        paperType.printTypes.push(
          new PrintType().deserialize({
            id: "standard",
            name: "Standard",
            allowed: true,
            finishes: ["Gloss", "Matte"],
          })
        );
      }
      if (selectedSubcategory.displayLuxe) {
        paperType.printTypes.push(
          new PrintType().deserialize({
            id: "luxe",
            name: "Luxe",
            allowed: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryWhite) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "kw",
            name: "Standard White",
            default: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryKraft) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "k",
            name: "Kraft",
            default: false,
            finishes: ["Matte"],
          })
        );
      }
      paperTypes.push(paperType);
    }
    if (selectedSubcategory.displayWhite) {
      const paperType = new PaperType().deserialize({
        id: "kw",
        name: "Standard White",
      });
      if (selectedSubcategory.displayStandard) {
        paperType.printTypes.push(
          new PrintType().deserialize({
            id: "standard",
            name: "Standard",
            allowed: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displayLuxe) {
        paperType.printTypes.push(
          new PrintType().deserialize({
            id: "luxe",
            name: "Luxe",
            allowed: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryPremiumWhite) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "pkw",
            name: "Premium White",
            default: false,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryWhite) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "kw",
            name: "Standard White",
            default: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryKraft) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "k",
            name: "Kraft",
            default: false,
            finishes: ["Matte"],
          })
        );
      }
      paperTypes.push(paperType);
    }
    if (selectedSubcategory.displayKraft) {
      const paperType = new PaperType().deserialize({
        id: "k",
        name: "Kraft",
      });
      if (selectedSubcategory.displayStandard) {
        paperType.printTypes.push(
          new PrintType().deserialize({
            id: "standard",
            name: "Standard",
            allowed: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displayLuxe) {
        paperType.printTypes.push(
          new PrintType().deserialize({
            id: "luxe",
            name: "Luxe",
            allowed: false,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryPremiumWhite) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "pkw",
            name: "Premium White",
            default: false,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryWhite) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "kw",
            name: "Standard White",
            default: true,
            finishes: ["Matte"],
          })
        );
      }
      if (selectedSubcategory.displaySecondaryKraft) {
        paperType.secondary.push(
          new SecondaryPaperType().deserialize({
            id: "k",
            name: "Kraft",
            default: false,
            finishes: ["Matte"],
          })
        );
      }
      paperTypes.push(paperType);
    }
    // console.log('paperTypes', paperTypes);
    return paperTypes;
  }
  listPaddedMailerPrices(code: string, paperType: string, quantities: number[]) {
    const url = `${this.environment.apiHost}/box/price/padded-mailer/${code}/${paperType}/list?quantities=${quantities.join(
      ","
    )}&rush=false&noPrint=false`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          (response.prices as any[]).forEach((price) => {
            this.setPaddedMailerPriceLabel(price, response.paddedMailer.type.subcategory.allowCustomSample);
          });
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getPaddedMailerPrice(code: string, paperType: string, quantity: number) {
    const url = `${this.environment.apiHost}/box/price/padded-mailer/${code}/${paperType}?quantity=${quantity}&rush=false&noPrint=false`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response: any) => {
          this.setPaddedMailerPriceLabel(response.price, response.type.subcategory.allowCustomSample);
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  setPaddedMailerPriceLabel(price: { quantity: number; label: string; pricePerUnit: any; unitsPerCase: string; }, allowCustomSample: any) {
    // console.log(`q ${price.quantity} ${price.quantity === 1 && subcategory.allowCustomSample}`);
    if (price.quantity === 1 && allowCustomSample) {
      price.label = `1 Custom Sample ${formatCurrency(price.pricePerUnit, "en-us", "$")}`;
    } else {
      price.label =
        formatNumber(price.quantity, "en-us") +
        " case(s) of " +
        price.unitsPerCase +
        " " +
        formatCurrency(price.pricePerUnit, "en-us", "$") +
        " / Mailer";
    }
  }

  listTissuePrices(quantities: any[], weight: number) {
    const url = `${this.environment.apiHost}/box/price/tissue/White/${weight}/list?quantities=${quantities.join(
      ","
    )}&rush=false`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getTissuePrice(quantity: number, weight: number) {
    const url = `${this.environment.apiHost}/box/price/tissue/White/${weight}?quantity=${quantity}&rush=false`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  listCrinklePrices(quantities: any[], color: string | undefined) {
    const url = `${this.environment.apiHost}/box/price/crinkle/${color}/list?quantities=${quantities.join(",")}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getCrinklePrice(quantity: string | number, color: string | undefined) {
    const url = `${this.environment.apiHost}/box/price/crinkle/${color}?quantity=${quantity}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  listLabelPrices(quantities: any[], shape: string, paperType: string, length: number, width: number, depth: number) {
    const url = `${this.environment.apiHost}/box/price/labels/${shape}/${paperType}/list?quantities=${quantities.join(
      ","
    )}&rush=false&length=${length}&width=${width}&depth=${depth}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getLabelPrice(quantity: string | number, shape: string, paperType: string, length: number, width: number, depth: number) {
    const url = `${this.environment.apiHost}/box/price/labels/${shape}/${paperType}?quantity=${quantity}&rush=false&length=${length}&width=${width}&depth=${depth}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (response) => {
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  listPartitionPrices(
    boardType: string,
    depth: number,
    length: number,
    lengthCells: number,
    paperType: string,
    quantities: any[],
    width: number,
    widthCells: number,
    lengthSlots: number,
    widthSlots: number
  ): Promise<PricingResponse> {
    const url = `${
      this.environment.apiHost
    }/box/price/partitions/${boardType}/${paperType}/list?depth=${depth}&length=${length}&lengthCells=${lengthCells}&quantities=${quantities.join(
      ","
    )}&rush=false&width=${width}&widthCells=${widthCells}&lengthSlots=${lengthSlots}&widthSlots=${widthSlots}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          const response = new PricingResponse().deserialize(res);
          resolve(response);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  getPartitionPrice(
    boardType: string,
    depth: number,
    length: number,
    lengthCells: number,
    paperType: string,
    quantity: string | number,
    width: number,
    widthCells: number,
    lengthSlots: number,
    widthSlots: number
  ) {
    const url = `${this.environment.apiHost}/box/price/partitions/${boardType}/${paperType}?depth=${depth}&length=${length}&lengthCells=${lengthCells}&quantity=${quantity}&rush=false&width=${width}&widthCells=${widthCells}&lengthSlots=${lengthSlots}&widthSlots=${widthSlots}`;

    return new Promise((resolve, reject) => {
      this.httpClient.get(url).subscribe(
        (res) => {
          resolve(res);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
}
